<template>
  <v-app-bar elevation="0" class="topbar">
    <v-btn
      icon
      @click="toggleSidebar"
      class="hamburger-btn"
      :class="{ 'mobile-only': !isMobile }"
      :aria-label="$t('topbar.toggleNavigation')"
      :title="$t('topbar.toggleNavigation')"
    >
      <v-icon>mdi-menu</v-icon>
    </v-btn>

    <v-toolbar-title class="mobile-title">
      <h1>{{ pageTitle }}</h1>
    </v-toolbar-title>

    <v-spacer></v-spacer>

    <div class="topbar-actions">
      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            icon
            v-bind="props"
            :title="languageButtonTitle"
            :aria-label="languageButtonTitle"
          >
            <v-icon>mdi-translate</v-icon>
          </v-btn>
        </template>
        <v-list
          density="compact"
          min-width="220"
          max-height="420"
          class="language-list"
        >
          <v-list-item
            :active="languageMode === 'auto'"
            @click="selectLanguage('auto')"
          >
            <template #prepend>
              <v-icon icon="mdi-web" size="small" />
            </template>
            <v-list-item-title>{{
              $t("topbar.languageAuto")
            }}</v-list-item-title>
          </v-list-item>
          <v-divider />
          <v-list-item
            v-for="lang in languages"
            :key="lang.code"
            :active="languageMode === lang.code"
            @click="selectLanguage(lang.code)"
          >
            <v-list-item-title>
              <span :lang="lang.code">{{ lang.nativeName }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            icon
            v-bind="props"
            :title="themeButtonTitle"
            :aria-label="themeButtonTitle"
          >
            <v-icon>{{ themeIcon }}</v-icon>
          </v-btn>
        </template>
        <v-list density="compact" min-width="180">
          <v-list-item
            v-for="option in themeOptions"
            :key="option.value"
            :active="themeMode === option.value"
            @click="selectTheme(option.value)"
          >
            <template #prepend>
              <v-icon :icon="option.icon" size="small" />
            </template>
            <v-list-item-title>{{ option.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Account menu. Was a bare link to /user with no way to sign out
           except the page footer, which a client user on a short page may
           never scroll to. -->
      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            icon
            v-bind="props"
            :aria-label="$t('topbar.account')"
            :title="$t('topbar.account')"
          >
            <v-icon>mdi-account-circle</v-icon>
          </v-btn>
        </template>
        <v-list density="compact" min-width="220">
          <v-list-item v-if="userEmail" :subtitle="userEmail">
            <v-list-item-title>{{ userName }}</v-list-item-title>
          </v-list-item>
          <v-divider v-if="userEmail" />
          <v-list-item to="/user" prepend-icon="mdi-account-cog">
            <v-list-item-title>{{
              $t("routes.userProfile")
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item prepend-icon="mdi-logout" @click="logout">
            <v-list-item-title>{{ $t("footer.logout") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-app-bar>
</template>

<script>
import axios from "axios";
import { themeMode, setThemeMode, resolveThemeName } from "@/theme";
import { ISI_API_URL } from "@/config";
import { logoutAndLeave } from "@/utils/logout";
import {
  languageMode,
  setLanguage,
  resolveLocale,
  SUPPORTED_LANGUAGES,
  appendLangParam,
  routeTitle,
} from "@/i18n";

const THEME_OPTIONS = [
  {
    value: "system",
    labelKey: "topbar.themeMatchSystem",
    icon: "mdi-theme-light-dark",
  },
  {
    value: "light",
    labelKey: "topbar.themeLight",
    icon: "mdi-white-balance-sunny",
  },
  { value: "dark", labelKey: "topbar.themeDark", icon: "mdi-weather-night" },
];

export default {
  name: "SimpleTopbar",
  props: {
    sidebarOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    // themeMode/languageMode are module-level refs shared with src/theme.js
    // and src/i18n, so the buttons stay correct even when changed elsewhere.
    return { themeMode, setThemeMode, languageMode, setLanguage };
  },
  data() {
    return {
      isMobile: false,
      languages: SUPPORTED_LANGUAGES,
    };
  },
  computed: {
    userEmail() {
      return this.$store.state.user_data.email || "";
    },
    userName() {
      const user = this.$store.state.user_data;
      const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
      return name || user.email || "";
    },
    // Computed (not data) so the labels re-render on language switch.
    themeOptions() {
      return THEME_OPTIONS.map((option) => ({
        value: option.value,
        icon: option.icon,
        label: this.$t(option.labelKey),
      }));
    },
    // Translated page title. $i18n.locale in the expression makes it
    // re-render on language switch (routeTitle itself is not reactive).
    pageTitle() {
      return this.$i18n.locale && routeTitle(this.$route);
    },
    // In "system" mode show the neutral half-moon rather than pretending the
    // user picked the theme the OS happens to be on.
    themeIcon() {
      if (this.themeMode === "system") {
        return "mdi-theme-light-dark";
      }
      return this.themeMode === "dark"
        ? "mdi-weather-night"
        : "mdi-white-balance-sunny";
    },
    themeButtonTitle() {
      if (this.themeMode === "system") {
        return this.$t("topbar.themeMatchingSystem", {
          name: this.themeLabelFor(resolveThemeName("system")),
        });
      }
      return this.$t("topbar.themeCurrent", {
        name: this.themeLabelFor(this.themeMode),
      });
    },
    languageButtonTitle() {
      const current = resolveLocale(this.languageMode);
      const info = this.languages.find((lang) => lang.code === current);
      const name = info ? info.nativeName : current;
      if (this.languageMode === "auto") {
        return (
          this.$t("topbar.language") +
          ": " +
          this.$t("topbar.languageAuto") +
          " (" +
          name +
          ")"
        );
      }
      return this.$t("topbar.language") + ": " + name;
    },
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 960;
    },
    logout() {
      logoutAndLeave();
    },
    // Translated label for a theme value ("light" → "Light", localized).
    themeLabelFor(value) {
      const option = THEME_OPTIONS.find((opt) => opt.value === value);
      return option ? this.$t(option.labelKey) : value;
    },
    toggleSidebar() {
      this.$emit("toggle-sidebar");
    },
    selectTheme(mode) {
      this.setThemeMode(mode);
    },
    selectLanguage(mode) {
      this.setLanguage(mode);
    },
  },
};
</script>

<style scoped>
.topbar {
  background: rgb(var(--v-theme-surface)) !important;
  border-bottom: 1px solid rgb(var(--v-theme-border-subtle));
}

.hamburger-btn {
  margin-right: 16px;
}

.mobile-only {
  display: none;
}

@media (max-width: 959px) {
  .mobile-only {
    display: flex;
  }
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.language-list {
  overflow-y: auto;
}

/* The page title is the document h1, styled like the old h3 so the visual
   weight is unchanged. */
.v-toolbar-title h1 {
  margin: 0;
  font-weight: 400;
  font-size: 1.17em;
  color: rgb(var(--v-theme-on-surface));
}

@media (max-width: 959px) {
  .mobile-title {
    flex: 1 1 auto !important;
    max-width: calc(100vw - 120px) !important;
  }

  .v-toolbar-title h1 {
    font-size: 0.9rem;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
